// apps/web/src/features/cargo/cargoPaymentReview.ts
//
// Pure helpers for the financier client-payment review view (#1938): per-field
// validation against the shared wire schemas, the local RUB preview, and the
// changed-decision builder. Kept framework-free so the view component stays
// small and the rounding/omission rules are unit-testable.

import {
  CargoExchangeRateSchema,
  CargoNonnegMoneySchema,
  type CargoPaymentCurrency,
  type CargoPaymentReviewDecision,
} from '@magimp/shared';

import { normalizeCargoMoneyInput } from './numeric.js';

/** The financier's locally-edited draft for one document, before it is sent. */
export interface PaymentReviewDraft {
  decision: 'pending' | 'accepted' | 'rejected';
  currency: CargoPaymentCurrency | '';
  /** Raw text buffers (RUB or foreign amount + rate), validated on submit. */
  receivedRub: string;
  foreignAmount: string;
  exchangeRate: string;
}

export function emptyReviewDraft(): PaymentReviewDraft {
  return {
    decision: 'pending',
    currency: '',
    receivedRub: '',
    foreignAmount: '',
    exchangeRate: '',
  };
}

/**
 * Canonicalise a money text buffer to the shared `\d+\.\d{2}` wire string, or
 * `null` when the buffer is blank/invalid (the caller treats `null` as "not yet
 * a valid amount").
 */
export function toMoneyString(value: string): string | null {
  const normalized = normalizeCargoMoneyInput(value);
  if (normalized == null) return null; // blank or non-money-like
  return CargoNonnegMoneySchema.safeParse(normalized).success ? normalized : null;
}

/** Canonical positive exchange-rate string, or `null` when blank/invalid. */
export function toRateString(value: string): string | null {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed.length === 0) return null;
  return CargoExchangeRateSchema.safeParse(trimmed).success ? trimmed : null;
}

/**
 * Local RUB preview for a foreign-currency accepted document:
 * `foreignAmount × exchangeRate`, rounded HALF-UP to 2 decimals, returned as a
 * `\d+\.\d{2}` string. Computed with BigInt scaled integers so the display
 * never drifts on large/precise values. Returns `null` when either input is
 * not yet valid.
 *
 * ponytail: display-only. The backend is authoritative for the stored
 * `receivedRub`; this preview just shows the manager-financier the figure they
 * are about to commit and never crosses the wire.
 */
export function previewRubString(amount: string, rate: string): string | null {
  const money = toMoneyString(amount);
  const rateStr = toRateString(rate);
  if (money == null || rateStr == null) return null;

  // money is `\d+.\d{2}` (scale 2). rate is `\d+(.\d{1,6})?` (scale ≤ 6).
  const amountScaled = BigInt(money.replace('.', '')); // value × 100
  const [rInt, rFrac = ''] = rateStr.split('.');
  const rateScale = rFrac.length;
  const rateScaled = BigInt(`${rInt}${rFrac}`); // value × 10^rateScale

  // product is value × 10^(2 + rateScale); we want value × 100 (2dp), half-up.
  const product = amountScaled * rateScaled;
  const dropDigits = rateScale; // bring 10^(2+rateScale) down to 10^2
  if (dropDigits === 0) {
    return scaledToMoney(product);
  }
  const divisor = 10n ** BigInt(dropDigits);
  const half = divisor / 2n;
  const roundedScaled = (product + half) / divisor; // half-up (inputs nonneg)
  return scaledToMoney(roundedScaled);
}

/** A `value × 100` BigInt → `\d+\.\d{2}` money string. */
function scaledToMoney(scaled: bigint): string {
  const s = scaled.toString().padStart(3, '0');
  const intPart = s.slice(0, -2);
  const fracPart = s.slice(-2);
  return `${intPart}.${fracPart}`;
}

/**
 * True when the draft is a complete, sendable accept (its required amounts
 * canonicalise). A non-accepted draft is trivially complete. This gates Save:
 * an accepted doc with empty/invalid amounts blocks it.
 */
export function isDraftComplete(draft: PaymentReviewDraft): boolean {
  if (draft.decision !== 'accepted') return true;
  if (draft.currency === '') return false;
  if (draft.currency === 'RUB') return toMoneyString(draft.receivedRub) !== null;
  return toMoneyString(draft.foreignAmount) !== null && toRateString(draft.exchangeRate) !== null;
}

/**
 * True when the draft differs from the document's persisted review state — only
 * changed documents are sent. A still-`pending` draft is never a change.
 */
export function isDraftChanged(
  draft: PaymentReviewDraft,
  persisted: { reviewStatus?: 'pending' | 'accepted' | 'rejected' | undefined } | undefined,
): boolean {
  if (draft.decision === 'pending') return false;
  return draft.decision !== (persisted?.reviewStatus ?? 'pending');
}

/**
 * Build the wire decision for one CHANGED draft, honouring the conditional
 * currency rules so an invalid shape is never sent:
 *   - rejected: no money/currency fields at all.
 *   - accepted RUB: `receivedRub` only.
 *   - accepted USD/CNY: `foreignAmount` + `exchangeRate` only (backend computes RUB).
 * Returns `null` when the draft is incomplete (caller blocks Save first).
 */
export function buildDecision(
  documentId: string,
  draft: PaymentReviewDraft,
): CargoPaymentReviewDecision | null {
  if (draft.decision === 'rejected') {
    return { documentId, decision: 'rejected' };
  }
  if (draft.decision !== 'accepted' || draft.currency === '') return null;
  if (draft.currency === 'RUB') {
    const receivedRub = toMoneyString(draft.receivedRub);
    if (receivedRub == null) return null;
    return { documentId, decision: 'accepted', currency: 'RUB', receivedRub };
  }
  const foreignAmount = toMoneyString(draft.foreignAmount);
  const exchangeRate = toRateString(draft.exchangeRate);
  if (foreignAmount == null || exchangeRate == null) return null;
  return {
    documentId,
    decision: 'accepted',
    currency: draft.currency,
    foreignAmount,
    exchangeRate,
  };
}
